import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import axios from "axios";
import { 
  RefreshCw,
  Loader2,
  User,
  Phone,
  Mail,
  Copy,
  ExternalLink,
  ChevronDown,
  Check,
  History,
  Wallet,
  Home,
  Settings,
  TrendingUp,
  BarChart3
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TEMPLATES = [
  { id: 'main', name: 'Обзор', icon: Home, description: 'Портфель и графики' },
  { id: 'history', name: 'История', icon: History, description: 'Торговые операции' },
  { id: 'settings', name: 'Настройки', icon: Settings, description: 'Профиль' }
];

export default function CryptoTradeTemplate() {
  const [profiles, setProfiles] = useState([]);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState('main');
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedData, setGeneratedData] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [combinedPages, setCombinedPages] = useState(false);
  const [browserTheme, setBrowserTheme] = useState('dark');
  const previewRef = useRef(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return { Authorization: `Bearer ${token}` };
  };

  const fetchProfiles = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/ozon/profiles`, {
        headers: getAuthHeaders(),
      });
      const profilesWithIds = response.data.map(profile => ({
        ...profile,
        visibleId: profile.visibleId || Math.floor(100000 + Math.random() * 900000)
      }));
      setProfiles(profilesWithIds);
    } catch (error) {
      console.error("Error fetching profiles:", error);
      toast.error("Ошибка загрузки профилей");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProfiles();
  }, [fetchProfiles]);

  const formatPhone = (phone) => {
    if (!phone) return "+7 (999) 123-45-67";
    const digits = phone.replace(/\D/g, '');
    if (digits.length === 11) {
      return `+${digits[0]} (${digits.slice(1,4)}) ${digits.slice(4,7)}-${digits.slice(7,9)}-${digits.slice(9,11)}`;
    }
    return phone;
  };

  const handleGenerate = () => {
    if (!selectedProfile) {
      toast.error("Выберите профиль OZON");
      return;
    }
    
    setIsGenerating(true);
    
    const firstName = selectedProfile.first_name || selectedProfile.accountName?.split(' ')[0] || selectedProfile.name?.split(' ')[0] || "Имя";
    const lastName = selectedProfile.last_name || selectedProfile.accountName?.split(' ')[1] || selectedProfile.name?.split(' ')[1] || "Фамилия";
    const phone = selectedProfile.phone || "+7 (999) 123-45-67";
    const email = selectedProfile.email && selectedProfile.email !== "не установлен" 
      ? selectedProfile.email 
      : `${firstName.toLowerCase()}.${lastName.toLowerCase()}@mail.ru`;
    
    // Get template balance in RUB
    const totalBalanceRub = selectedProfile.template_balance || selectedProfile.balance || 100000;
    
    // Distribute balance across cryptocurrencies
    const btcRate = 6500000;
    const ethRate = 250000;
    const usdtRate = 95;
    
    const btcPercent = 0.3 + Math.random() * 0.2;
    const ethPercent = 0.2 + Math.random() * 0.15;
    const usdtPercent = 0.15 + Math.random() * 0.2;
    const rubPercent = 1 - btcPercent - ethPercent - usdtPercent;
    
    const btcBalance = ((totalBalanceRub * btcPercent) / btcRate).toFixed(6);
    const ethBalance = ((totalBalanceRub * ethPercent) / ethRate).toFixed(4);
    const usdtBalance = ((totalBalanceRub * usdtPercent) / usdtRate).toFixed(2);
    const rubBalance = Math.floor(totalBalanceRub * rubPercent);

    const transactions = (selectedProfile.transactions || selectedProfile.operations || []).map(tx => ({
      type: tx.type || (tx.amount > 0 ? 'buy' : 'sell'),
      amount: Math.abs(tx.amount || Math.floor(Math.random() * 5000)).toString(),
      currency: ['USDT', 'BTC', 'ETH'][Math.floor(Math.random() * 3)],
      price: (Math.random() * 100000).toFixed(2),
      date: tx.date || new Date().toLocaleDateString('ru-RU'),
      time: tx.time || '12:00',
      status: 'completed'
    }));

    // Generate some trades if none
    if (transactions.length === 0) {
      for (let i = 0; i < 5; i++) {
        transactions.push({
          type: Math.random() > 0.5 ? 'buy' : 'sell',
          amount: (Math.random() * 5000 + 100).toFixed(2),
          currency: ['USDT', 'BTC', 'ETH'][Math.floor(Math.random() * 3)],
          price: (Math.random() * 100000).toFixed(2),
          date: new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000).toLocaleDateString('ru-RU'),
          time: `${Math.floor(Math.random() * 24).toString().padStart(2, '0')}:${Math.floor(Math.random() * 60).toString().padStart(2, '0')}`,
          status: 'completed'
        });
      }
    }
    
    const data = {
      visibleId: selectedProfile.visibleId,
      firstName,
      lastName,
      fullName: `${firstName} ${lastName}`,
      phone: formatPhone(phone),
      email,
      balance: rubBalance.toString(),
      totalBalanceRub: totalBalanceRub.toString(),
      usdtBalance,
      btcBalance,
      ethBalance,
      transactions,
      browserTheme
    };
    
    setGeneratedData(data);
    setIsGenerating(false);
    toast.success("Данные сгенерированы!");
  };

  const getPreviewUrl = () => {
    if (!generatedData) return '';
    const dataStr = encodeURIComponent(JSON.stringify(generatedData));
    
    if (combinedPages) {
      return `${window.location.origin}/templates/cryptotrade/combined?data=${dataStr}&theme=${browserTheme}`;
    }
    
    return `${window.location.origin}/templates/cryptotrade/${selectedTemplate}?data=${dataStr}&theme=${browserTheme}`;
  };

  const openPreview = () => {
    const url = getPreviewUrl();
    if (url) {
      window.open(url, '_blank');
    }
  };

  const copyLink = () => {
    const url = getPreviewUrl();
    if (url) {
      navigator.clipboard.writeText(url);
      toast.success("Ссылка скопирована!");
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="animate-fade-in" data-testid="cryptotrade-template-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg">
              <TrendingUp className="w-5 h-5 text-white" />
            </div>
            CryptoTrade Биржа
          </h1>
          <p className="text-slate-500 mt-1">
            Генератор страницы криптобиржи из профиля OZON
          </p>
        </div>
        <Button 
          onClick={() => fetchProfiles()}
          variant="outline"
          data-testid="refresh-profiles-btn"
        >
          <RefreshCw className="w-4 h-4" />
          Обновить профили
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Panel - Settings */}
        <Card>
          <CardContent className="p-6">
            <h2 className="text-lg font-semibold text-slate-800 mb-4">Настройки генерации</h2>
            
            {/* Template Selection */}
            <div className="space-y-3 mb-6">
              <Label className="text-slate-700">Выберите шаблон</Label>
              <div className="grid grid-cols-3 gap-3">
                {TEMPLATES.map((template) => {
                  const Icon = template.icon;
                  return (
                    <button
                      key={template.id}
                      onClick={() => {
                        setSelectedTemplate(template.id);
                        setGeneratedData(null);
                      }}
                      className={`p-4 rounded-lg border-2 transition-all text-left ${
                        selectedTemplate === template.id
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-slate-200 bg-white hover:border-slate-300'
                      }`}
                    >
                      <Icon className={`w-6 h-6 mb-2 ${
                        selectedTemplate === template.id ? 'text-blue-500' : 'text-slate-400'
                      }`} />
                      <div className={`font-medium ${
                        selectedTemplate === template.id ? 'text-blue-700' : 'text-slate-700'
                      }`}>
                        {template.name}
                      </div>
                      <div className="text-xs text-slate-500 mt-1">{template.description}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Combined Pages Option */}
            <div className="mb-6">
              <label className="flex items-center gap-3 p-3 rounded-lg border-2 border-slate-200 bg-white hover:border-blue-300 cursor-pointer transition-colors">
                <input
                  type="checkbox"
                  checked={combinedPages}
                  onChange={(e) => {
                    setCombinedPages(e.target.checked);
                    setGeneratedData(null);
                  }}
                  className="w-5 h-5 rounded border-slate-300 text-blue-500 focus:ring-blue-500"
                />
                <div>
                  <div className="font-medium text-slate-700">Объединить все страницы</div>
                  <div className="text-xs text-slate-500">
                    Все страницы в одной ссылке с навигацией через меню
                  </div>
                </div>
              </label>
            </div>

            {/* Browser Theme */}
            <div className="space-y-3 mb-6">
              <Label className="text-slate-700">Тема браузера</Label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => { setBrowserTheme('light'); setGeneratedData(null); }}
                  className={`p-3 rounded-lg border-2 transition-all ${
                    browserTheme === 'light'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-slate-200 bg-white hover:border-slate-300'
                  }`}
                >
                  <span className={browserTheme === 'light' ? 'text-blue-700' : 'text-slate-700'}>☀️ Светлая</span>
                </button>
                <button
                  onClick={() => { setBrowserTheme('dark'); setGeneratedData(null); }}
                  className={`p-3 rounded-lg border-2 transition-all ${
                    browserTheme === 'dark'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-slate-200 bg-white hover:border-slate-300'
                  }`}
                >
                  <span className={browserTheme === 'dark' ? 'text-blue-700' : 'text-slate-700'}>🌙 Тёмная</span>
                </button>
              </div>
            </div>

            {/* Profile Selection */}
            <div className="space-y-3 mb-6">
              <Label className="text-slate-700">Профиль OZON</Label>
              <div className="relative">
                <button
                  data-testid="profile-dropdown"
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="w-full p-3 rounded-lg border-2 border-slate-200 bg-white text-left flex items-center justify-between hover:border-slate-300 transition-colors"
                >
                  {selectedProfile ? (
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                        <User className="w-4 h-4 text-blue-600" />
                      </div>
                      <div>
                        <div className="text-slate-800 font-medium">{selectedProfile.accountName || selectedProfile.name}</div>
                        <div className="text-xs text-slate-500">
                          ID: {selectedProfile.visibleId} • Баланс: {(selectedProfile.template_balance || selectedProfile.balance || 0).toLocaleString()} ₽
                        </div>
                      </div>
                    </div>
                  ) : (
                    <span className="text-slate-400">Выберите профиль...</span>
                  )}
                  <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
                </button>

                {dropdownOpen && (
                  <div className="absolute z-50 w-full mt-2 py-2 bg-white border border-slate-200 rounded-lg shadow-xl max-h-64 overflow-y-auto">
                    {profiles.length === 0 ? (
                      <div className="px-4 py-3 text-slate-400 text-center">
                        Нет профилей
                      </div>
                    ) : (
                      profiles.map((profile) => (
                        <button
                          key={profile._id || profile.id}
                          onClick={() => {
                            setSelectedProfile(profile);
                            setDropdownOpen(false);
                            setGeneratedData(null);
                          }}
                          className={`w-full px-4 py-3 flex items-center gap-3 hover:bg-slate-50 transition-colors ${
                            selectedProfile?._id === profile._id || selectedProfile?.id === profile.id ? 'bg-blue-50' : ''
                          }`}
                        >
                          <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
                            <User className="w-4 h-4 text-slate-500" />
                          </div>
                          <div className="text-left flex-1">
                            <div className="text-slate-800">{profile.accountName || profile.name}</div>
                            <div className="text-xs text-slate-500">
                              ID: {profile.visibleId} • {(profile.template_balance || profile.balance || 0).toLocaleString()} ₽ • {(profile.transactions || profile.operations || []).length} операций
                            </div>
                          </div>
                          {(selectedProfile?._id === profile._id || selectedProfile?.id === profile.id) && (
                            <Check className="w-4 h-4 text-blue-500" />
                          )}
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Generate Button */}
            <Button 
              onClick={handleGenerate}
              disabled={!selectedProfile || isGenerating}
              className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white"
              data-testid="generate-btn"
            >
              {isGenerating ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <BarChart3 className="w-4 h-4 mr-2" />
              )}
              Сгенерировать
            </Button>
          </CardContent>
        </Card>

        {/* Right Panel - Preview */}
        <Card>
          <CardContent className="p-6">
            <h2 className="text-lg font-semibold text-slate-800 mb-4">Предпросмотр данных</h2>
            
            {generatedData ? (
              <div className="space-y-4">
                {/* Preview Info */}
                <div className="p-4 bg-slate-50 rounded-lg space-y-3">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-600">Имя:</span>
                    <span className="text-slate-800 font-medium">{generatedData.fullName}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-600">Телефон:</span>
                    <span className="text-slate-800">{generatedData.phone}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-600">Email:</span>
                    <span className="text-slate-800">{generatedData.email}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Wallet className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-600">ID:</span>
                    <span className="text-slate-800">{generatedData.visibleId}</span>
                  </div>
                  <div className="pt-2 border-t border-slate-200">
                    <div className="text-sm text-slate-600 mb-1">Портфель:</div>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div><span className="text-green-600">USDT:</span> ${generatedData.usdtBalance}</div>
                      <div><span className="text-orange-600">BTC:</span> {generatedData.btcBalance}</div>
                      <div><span className="text-blue-600">ETH:</span> {generatedData.ethBalance}</div>
                      <div><span className="text-slate-600">RUB:</span> {parseFloat(generatedData.balance).toLocaleString()}</div>
                    </div>
                  </div>
                  <div className="pt-2 border-t border-slate-200">
                    <span className="text-slate-600">Сделок:</span>
                    <span className="text-slate-800 ml-2">{generatedData.transactions.length}</span>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3">
                  <Button 
                    onClick={openPreview}
                    className="flex-1 bg-blue-500 hover:bg-blue-600 text-white"
                    data-testid="open-preview-btn"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Открыть
                  </Button>
                  <Button 
                    onClick={copyLink}
                    variant="outline"
                    className="flex-1"
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    Копировать ссылку
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4">
                  <TrendingUp className="w-8 h-8 text-slate-300" />
                </div>
                <p className="text-slate-500">
                  Выберите профиль и нажмите «Сгенерировать»
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
